import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { RefreshCw, MessageSquareWarning } from 'lucide-react';

import { Button } from '@/components/ui/button';

const API_URL = process.env.REACT_APP_API_URL;
const STATUS_LABEL = {
  open: 'Açık',
  in_progress: 'İnceleniyor',
  resolved: 'Çözüldü',
  closed: 'Kapalı',
};

export function SupportTicketsPanel() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter ? { status: filter } : {};
      const { data } = await axios.get(`${API_URL}/api/support/superadmin/tickets`, { params, withCredentials: true });
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Destek talepleri yüklenemedi.');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API_URL}/api/support/superadmin/tickets/${id}/status`, { status }, { withCredentials: true });
      setItems((prev) => prev.map((x) => x.id === id ? { ...x, status } : x));
      toast.success('Destek talebi güncellendi.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Durum güncellenemedi.');
    }
  };

  return (
    <div data-testid="sa-support-tickets">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-[#1A1A2E]">Destek Talepleri</h1>
          <p className="text-sm text-[#595959] mt-1">Uygulama içindeki “Sorun Bildir” formundan gelen kayıtlar.</p>
        </div>
        <Button variant="outline" onClick={load} className="gap-2">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Yenile
        </Button>
      </div>

      <div className="mb-4">
        <select value={filter} onChange={(e) => setFilter(e.target.value)} className="h-10 rounded-md border border-[#E5E5E5] bg-white px-3 text-sm">
          <option value="">Tüm durumlar</option>
          <option value="open">Açık</option>
          <option value="in_progress">İnceleniyor</option>
          <option value="resolved">Çözüldü</option>
          <option value="closed">Kapalı</option>
        </select>
      </div>

      <div className="space-y-3">
        {!loading && items.length === 0 && (
          <div className="rounded-xl border border-[#E5E5E5] bg-white p-8 text-center text-[#64748B]">
            <MessageSquareWarning className="w-8 h-8 mx-auto mb-2 opacity-50" />
            Destek talebi yok.
          </div>
        )}
        {items.map((ticket) => (
          <div key={ticket.id} className="rounded-xl border border-[#E5E5E5] bg-white p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="font-semibold text-[#1A1A2E]">{ticket.subject}</h3>
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-700">{STATUS_LABEL[ticket.status] || ticket.status}</span>
                  <span className="rounded-full bg-blue-50 px-2 py-0.5 text-xs text-blue-700">{ticket.category}</span>
                </div>
                <p className="text-xs text-[#64748B] mt-1">{ticket.user_name || '—'} · {ticket.user_email || '—'}</p>
                <p className="mt-3 whitespace-pre-wrap text-sm text-[#334155]">{ticket.message}</p>
              </div>
              <select
                value={ticket.status || 'open'}
                onChange={(e) => setStatus(ticket.id, e.target.value)}
                className="h-9 rounded-md border border-[#E5E5E5] bg-white px-2 text-sm"
              >
                <option value="open">Açık</option>
                <option value="in_progress">İnceleniyor</option>
                <option value="resolved">Çözüldü</option>
                <option value="closed">Kapalı</option>
              </select>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default SupportTicketsPanel;
